import {
	AllCommunityModule,
	ColDef,
	ModuleRegistry,
	SizeColumnsToContentStrategy,
	themeAlpine,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import { useTheme } from 'styled-components';

import * as graphql from '@treams/treams-network';
import { ConnectorType } from '@treams/treams-network';
import { Badge, Heading3, Input } from '@treams/treams-ui';

import { ConnectorUser, GridRef, WorkspaceAdvanced, WorkspaceUsers } from './types';
import { columnSorter } from './utils';

const DISABLED_CONNECTOR_TYPES = [ConnectorType.Scim];

const defaultColDef: ColDef = {
	sortable: true,
	filter: true,
	floatingFilter: false,
	comparator: (valueA, valueB) => {
		if (valueA == null && valueB == null) return 0;
		if (valueA == null) return -1;
		if (valueB == null) return 1;

		const a = String(valueA).toLowerCase();
		const b = String(valueB).toLowerCase();

		return a.localeCompare(b);
	},
};

ModuleRegistry.registerModules([AllCommunityModule]);

const autoSizeStrategy: SizeColumnsToContentStrategy = { type: 'fitCellContents' };

export default function WorkspaceConnector({ workspace }: { workspace: WorkspaceAdvanced }) {
	const { t } = useTranslation();
	const theme = useTheme();
	const [filter, setFilter] = useState('');
	const cognitoGrid: GridRef = useRef(null);
	const connectorGrid: GridRef = useRef(null);
	const [cognitoRowCount, setCognitoRowCount] = useState(0);
	const [connectorRowCount, setConnectorRowCount] = useState(0);
	const gridTheme = themeAlpine.withParams({ accentColor: theme.colors.primary });

	const onConnectorFilterChanged = useCallback(() => {
		if (!connectorGrid.current?.api) return;

		setConnectorRowCount(connectorGrid.current.api.getDisplayedRowCount());
	}, []);

	const onCognitoFilterChanged = useCallback(() => {
		if (!cognitoGrid.current?.api) return;

		setCognitoRowCount(cognitoGrid.current.api.getDisplayedRowCount());
	}, []);

	const onFilter = useCallback((value: string) => {
		setFilter(value);

		connectorGrid.current?.api?.setGridOption('quickFilterText', value);

		cognitoGrid.current?.api?.setGridOption('quickFilterText', value);
	}, []);

	const connector = useMemo(() => {
		if (!workspace.connectorAdmin?.type) return t('common.none');

		return workspace.connectorAdmin?.type;
	}, [workspace.connectorAdmin?.type, t]);

	const query = graphql.useWorkspaceConnectorUsersQuery({
		variables: { id: workspace.id },
		skip: !workspace.id,
	});

	const { connectorUsers, cognitoUsers }: WorkspaceUsers = useMemo(() => {
		const data = query.data?.workspace;

		if (!data) return { connectorUsers: undefined, cognitoUsers: undefined };

		const connectorUsers = data.connectorUsers.map((user) =>
			Object.entries(user).reduce(
				(user, [key, value]) => {
					user[key] = typeof value !== 'object' ? `${value}` : JSON.stringify(value);

					return user;
				},
				{} as Record<string, string>,
			),
		);

		return { connectorUsers, cognitoUsers: data.cognitoUsers };
	}, [query.data]);

	const disabled = useMemo(() => {
		return (
			!workspace.connectorAdmin?.type ||
			DISABLED_CONNECTOR_TYPES.includes(workspace.connectorAdmin?.type)
		);
	}, [workspace.connectorAdmin?.type]);

	const connectorColumns = useMemo<ColDef<ConnectorUser>[]>(
		() =>
			columnSorter(
				Object.keys(connectorUsers?.[0] || {}).map((key) => ({
					field: key,
				})),
			),
		[connectorUsers],
	);

	const cognitoColumns = useMemo<ColDef<ConnectorUser>[]>(
		() =>
			columnSorter(
				Object.keys(cognitoUsers?.[0] || {}).reduce((columns, key) => {
					if (key !== 'Attributes') {
						columns.push({ field: key });

						return columns;
					}

					const attrs = (cognitoUsers?.[0]?.Attributes ?? []) as { Name: string; Value: string }[];

					attrs?.forEach((attr) => {
						columns.push({
							field: attr.Name,
							headerName: `${attr.Name[0].toUpperCase()}${attr.Name.slice(1)}`.replace(/_/g, ' '),
							valueGetter: (params) => {
								const attrs = params.data?.Attributes as { Name: string; Value: string }[];

								return attrs.find((a) => a.Name === attr.Name)?.Value;
							},
						});
					});

					return columns;
				}, [] as ColDef<ConnectorUser>[]),
			),
		[cognitoUsers],
	);

	if (!workspace.connectorAdmin) return null;

	return (
		<Box display="flex" flexDirection="column" p={4} flexGrow={1}>
			<Box display="flex" px={4} mb={4} maxHeight={50}>
				<Box flexGrow={1} flexBasis={0} display="flex" alignItems="center">
					<Badge color={workspace.connectorAdmin?.isActive ? 'green' : 'red'} />
					<Heading3 ml={1}>
						{connector}{' '}
						{workspace.connectorAdmin?.isActive && !disabled && `(${connectorRowCount})`}
					</Heading3>
				</Box>
				{workspace.connectorAdmin?.isActive && (
					<Box flexGrow={1} flexBasis={0} display="flex" alignItems="center">
						<Input
							size="small"
							value={filter}
							onChange={onFilter}
							placeholder={t('common.search')}
						/>
					</Box>
				)}
				<Box flexGrow={1} flexBasis={0} display="flex" alignItems="center">
					<Heading3 ml="auto" mt="2px">
						Treams ({cognitoRowCount})
					</Heading3>
				</Box>
			</Box>
			<Box display="flex" flexGrow={1}>
				{workspace.connectorAdmin?.isActive && !disabled && (
					<Box flexGrow={1} flexBasis={0} minHeight={400}>
						<AgGridReact
							theme={gridTheme}
							ref={connectorGrid}
							rowData={connectorUsers}
							columnDefs={connectorColumns}
							defaultColDef={defaultColDef}
							autoSizeStrategy={autoSizeStrategy}
							enableCellTextSelection
							onFilterChanged={onConnectorFilterChanged}
							onModelUpdated={onConnectorFilterChanged}
						/>
					</Box>
				)}
				<Box minWidth={16} height="100%" />
				{workspace.connectorAdmin?.isActive && (
					<Box flexGrow={1} flexBasis={0} minHeight={400}>
						<AgGridReact
							theme={gridTheme}
							ref={cognitoGrid}
							rowData={cognitoUsers}
							columnDefs={cognitoColumns}
							defaultColDef={defaultColDef}
							autoSizeStrategy={autoSizeStrategy}
							enableCellTextSelection
							onFilterChanged={onCognitoFilterChanged}
							onModelUpdated={onCognitoFilterChanged}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
}
